import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, ArrowLeft, Clock, Users, Star, Copy } from 'lucide-react';

function PurchasePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [copiedItem, setCopiedItem] = useState('');

  // Kurs verileri - gerçek uygulamada API'den gelecek
  const coursesData = {
    1: {
      title: "Entrepreneurship & Business Idea Development",
      category: "Entrepreneurship",
      duration: "8 weeks",
      students: 0,
      rating: 4.9,
      price: "$497",
      features: [
        "Business Model Canvas",
        "Market Research",
        "MVP Development",
        "Customer Validation"
      ]
    },
    2: {
      title: "Company Formation, Trade & Financial Management",
      category: "Business Setup",
      duration: "6 weeks",
      students: 0,
      rating: 4.8,
      price: "$397",
      features: [
        "Legal Structure",
        "Financial Planning",
        "Tax Compliance",
        "Business Registration"
      ]
    },
    3: {
      title: "E-Export, Digital Marketing & Sales",
      category: "Global Expansion",
      duration: "10 weeks",
      students: 0,
      rating: 4.9,
      price: "$597",
      features: [
        "Global Market Entry",
        "Digital Marketing",
        "E-commerce",
        "International Sales"
      ]
    },
    4: {
      title: "Growth, Investment & Globalization",
      category: "Growth & Investment",
      duration: "12 weeks",
      students: 0,
      rating: 4.9,
      price: "$697",
      features: [
        "Scaling Strategies",
        "Investment Pitching",
        "Global Operations",
        "Strategic Partnerships"
      ]
    }
  };

  const course = coursesData[id] || coursesData[1];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simüle edilmiş gönderim
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  const copyToClipboard = async (type) => {
    let textToCopy = '';
    
    if (type === 'subject') {
      textToCopy = `Course Purchase Request - ${course.title}`;
    } else if (type === 'body') {
      textToCopy = `Dear Enes,

I hope this message finds you well.

I am writing to express my interest in enrolling in one of your premium courses. Please find the relevant details below:

**Course Information:**
• Title: ${course.title}
• Price: ${course.price}
• Category: ${course.category}
• Duration: ${course.duration}

**Customer Information:**
• Full Name: ${formData.name}
• Email: ${formData.email}
• Company: ${formData.company}

**Additional Notes:**
${formData.message}

I would like to proceed with the purchase and would appreciate it if you could kindly share the payment details and the next steps in the enrollment process.

Thank you for your time and consideration. I look forward to your response.

Best regards,
${formData.name}`;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setCopiedItem(type);
      setTimeout(() => {
        setIsCopied(false);
        setCopiedItem('');
      }, 2000);
    } catch (err) {
      console.error('Kopyalama hatası:', err);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star 
          key={i} 
          className={`w-5 h-5 ${i < Math.floor(rating) ? 'fill-current text-yellow-500' : 'text-slate-300'}`} 
        />
      );
    }
    return stars;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
                              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Email Ready! 📧
                </h1>
                <p className="text-slate-600 text-lg mb-6">
                  Your course purchase request email is ready to send. Copy the subject and body below.
                </p>
                
                {/* Email Subject */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4 text-left">
                  <h3 className="font-semibold text-blue-900 mb-2">📌 Email Subject:</h3>
                  <div className="bg-white rounded p-3 text-sm text-slate-700 mb-3">
                    Course Purchase Request - {course.title}
                  </div>
                  <button
                    onClick={() => copyToClipboard('subject')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                      isCopied && copiedItem === 'subject'
                        ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                    <span>{isCopied && copiedItem === 'subject' ? 'Copied!' : 'Copy Subject'}</span>
                  </button>
                </div>

                {/* Email Body */}
                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-green-900 mb-2">📝 Email Body:</h3>
                  <div className="bg-white rounded p-3 text-sm text-slate-700 whitespace-pre-line mb-3 font-mono">
                    Dear Enes,

I hope this message finds you well.

I am writing to express my interest in enrolling in one of your premium courses. Please find the relevant details below:

<span className="font-bold">Course Information:</span>
• Title: {course.title}
• Price: {course.price}
• Category: {course.category}
• Duration: {course.duration}

<span className="font-bold">Customer Information:</span>
• Full Name: {formData.name}
• Email: {formData.email}
• Company: {formData.company}

<span className="font-bold">Additional Notes:</span>
{formData.message}

I would like to proceed with the purchase and would appreciate it if you could kindly share the payment details and the next steps in the enrollment process.

Thank you for your time and consideration. I look forward to your response.

Best regards,
{formData.name}
                  </div>
                  <button
                    onClick={() => copyToClipboard('body')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                      isCopied && copiedItem === 'body'
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    <Copy className="w-4 h-4" />
                    <span>{isCopied && copiedItem === 'body' ? 'Copied!' : 'Copy Email Body'}</span>
                  </button>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2">📋 How to send:</h3>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>1. Copy the subject above</li>
                    <li>2. Copy the email body above</li>
                    <li>3. Open your email app</li>
                    <li>4. Send to: <strong>enes.ucar.2535@gmail.com</strong></li>
                    <li>5. We'll respond within 24 hours</li>
                  </ul>
                </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Geri Dön Butonu */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-slate-600 hover:text-blue-600 mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
                              <span>Go Back</span>
          </motion.button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Kurs Bilgileri */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Course Details</h2>
              
              {/* Kurs Başlığı */}
              <div className="mb-6">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                  {course.category}
                </span>
                <h3 className="text-xl font-bold text-slate-900 mt-3">
                  {course.title}
                </h3>
              </div>

              {/* Kurs Metrikleri */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center space-x-2 text-slate-600">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span>{course.students.toLocaleString()}</span>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-2 mb-6">
                {renderStars(course.rating)}
                <span className="text-slate-600 font-medium">
                  {course.rating} ({course.students.toLocaleString()})
                </span>
              </div>

              {/* Özellikler */}
              <div className="mb-6">
                <h4 className="font-semibold text-slate-900 mb-3">Course Content:</h4>
                <ul className="space-y-2">
                  {course.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Fiyat */}
              <div className="border-t border-slate-200 pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">{course.price}</div>
                <div className="text-slate-600">Lifetime Access</div>
              </div>
            </motion.div>

            {/* Satın Alma Formu */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Course Purchase
                  </h2>
                  <p className="text-slate-600">
                    Fill in your information and we'll get back to you as soon as possible
                  </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                </div>

                <div>
                                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="example@email.com"
                    />
                </div>


                <div>
                                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      Company Name (Optional)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                </div>

                <div>
                                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Any questions about the course or special requests..."
                    />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                                              <span>Send Purchase Request</span>
                    </>
                  )}
                </button>

                <div className="text-center text-sm text-slate-500">
                  <p>Your information is securely protected</p>
                  <p>We'll get back to you within 24 hours</p>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchasePage;
